import { useState, useEffect, useRef } from "react";
import { Check, ChevronDown, Camera, User, MapPin, FileText, Upload, Music, Heart, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { triggerPointsAnimation } from "@/components/PointsRewardAnimation";
import { triggerQuestComplete } from "@/components/QuestCompleteCelebration";

interface Quest {
  id: string;
  label: string;
  points: number;
  icon: React.ElementType;
  completed: boolean;
  awarded: boolean;
  action: () => void;
}

/** Convert a short string quest id into a deterministic UUID v5-like format */
const questIdToUuid = (id: string): string => {
  // Pad/hash the string into a 32-char hex, then format as UUID
  let hex = "";
  for (let i = 0; i < 32; i++) {
    hex += id.charCodeAt(i % id.length).toString(16).padStart(2, "0").slice(0, 2);
  }
  hex = hex.slice(0, 32);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
};

const QuestChecklist = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [quests, setQuests] = useState<Quest[]>([]);
  const [loading, setLoading] = useState(true);
  const prevCompletedRef = useRef<Set<string>>(new Set());
  const hasInitialized = useRef(false);

  const checkQuests = async () => {
    if (!user) return;

    const [profileRes, postsRes, concertsRes, fansRes, ledgerRes] = await Promise.all([
      supabase.from("profiles").select("display_name, bio, location, avatar_url").eq("user_id", user.id).maybeSingle(),
      supabase.from("posts").select("id", { count: "exact", head: true }).eq("user_id", user.id),
      supabase.from("concert_history").select("id", { count: "exact", head: true }).eq("user_id", user.id),
      supabase.from("artist_fans").select("id", { count: "exact", head: true }).eq("user_id", user.id),
      supabase.from("points_ledger").select("source_row_id").eq("user_id", user.id).eq("source_table", "quests"),
    ]);

    const p = profileRes.data;
    const awardedQuestUuids = new Set((ledgerRes.data || []).map((r: any) => r.source_row_id));

    const questList: Quest[] = [
      {
        id: "display_name",
        label: "Set your display name",
        points: 25,
        icon: User,
        completed: !!p?.display_name?.trim(),
        awarded: awardedQuestUuids.has(questIdToUuid("display_name")),
        action: () => navigate("/profile"),
      },
      {
        id: "avatar",
        label: "Upload a profile photo",
        points: 50,
        icon: Camera,
        completed: !!p?.avatar_url?.trim(),
        awarded: awardedQuestUuids.has(questIdToUuid("avatar")),
        action: () => navigate("/profile"),
      },
      {
        id: "bio",
        label: "Write a bio",
        points: 25,
        icon: FileText,
        completed: !!p?.bio?.trim(),
        awarded: awardedQuestUuids.has(questIdToUuid("bio")),
        action: () => navigate("/profile"),
      },
      {
        id: "location",
        label: "Add your location",
        points: 25,
        icon: MapPin,
        completed: !!p?.location?.trim(),
        awarded: awardedQuestUuids.has(questIdToUuid("location")),
        action: () => navigate("/profile"),
      },
      {
        id: "first_clip",
        label: "Upload your first clip",
        points: 150,
        icon: Upload,
        completed: (postsRes.count ?? 0) > 0,
        awarded: awardedQuestUuids.has(questIdToUuid("first_clip")),
        action: () => navigate("/upload"),
      },
      {
        id: "first_concert",
        label: "Add a concert to history",
        points: 25,
        icon: Music,
        completed: (concertsRes.count ?? 0) > 0,
        awarded: awardedQuestUuids.has(questIdToUuid("first_concert")),
        action: () => navigate("/profile"),
      },
      {
        id: "follow_artist",
        label: "Follow an artist",
        points: 25,
        icon: Heart,
        completed: (fansRes.count ?? 0) > 0,
        awarded: awardedQuestUuids.has(questIdToUuid("follow_artist")),
        action: () => navigate("/search"),
      },
    ];

    // Award points for newly completed quests
    const newlyCompleted = questList.filter((q) => q.completed && !q.awarded);

    for (const quest of newlyCompleted) {
      try {
        await supabase.rpc("award_republic_points", {
          _user_id: user.id,
          _actor_user_id: user.id,
          _event_type: "quest_completion" as any,
          _points: quest.points,
          _source_table: "quests",
          _source_row_id: questIdToUuid(quest.id),
        });

        quest.awarded = true;

        if (hasInitialized.current) {
          setTimeout(() => {
            triggerPointsAnimation(quest.points);
          }, newlyCompleted.indexOf(quest) * 600);
        }
      } catch (e) {
        console.error("Failed to award quest points:", quest.id, e);
      }
    }

    hasInitialized.current = true;
    setQuests(questList);
    setLoading(false);

    // If all quests just completed (not on first load), trigger celebration
    const allComplete = questList.every((q) => q.completed);
    const wasAllComplete = prevCompletedRef.current.size === questList.length;
    if (allComplete && newlyCompleted.length > 0 && !wasAllComplete) {
      setTimeout(() => triggerQuestComplete(), newlyCompleted.length * 600 + 400);
    }

    // Track completed set
    prevCompletedRef.current = new Set(questList.filter((q) => q.completed).map((q) => q.id));
  };

  // Initial check + listen for realtime changes on relevant tables
  useEffect(() => {
    if (!user) return;

    checkQuests();

    // Subscribe to profile, posts, concert_history, and artist_fans changes for this user
    const channel = supabase
      .channel(`quests-${user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles', filter: `user_id=eq.${user.id}` }, () => checkQuests())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'posts', filter: `user_id=eq.${user.id}` }, () => checkQuests())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'concert_history', filter: `user_id=eq.${user.id}` }, () => checkQuests())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'artist_fans', filter: `user_id=eq.${user.id}` }, () => checkQuests())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, navigate]);

  if (!user || loading) return null;

  // Hide button entirely when all quests are done
  const allDone = quests.length > 0 && quests.every((q) => q.completed && q.awarded);
  if (allDone) return null;

  const completed = quests.filter((q) => q.completed).length;
  const total = quests.length;
  const incomplete = total - completed;
  const progress = Math.round((completed / total) * 100);

  const handleDismiss = () => {
    setOpen(false);
    if (user) sessionStorage.setItem(`quests_dismissed_${user.id}`, "true");
  };

  return (
    <div className="relative">
      {/* Quest badge / trigger */}
      <button
        onClick={() => setOpen(!open)}
        className="relative flex items-center justify-center h-8 w-8 rounded-full bg-primary/10 hover:bg-primary/20 transition-all"
      >
        <Sparkles className="h-4 w-4 text-primary" />
        {incomplete > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
            {incomplete}
          </span>
        )}
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={handleDismiss} />
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="fixed right-3 left-3 sm:left-auto sm:absolute sm:right-0 top-16 sm:top-full sm:mt-2 z-50 sm:w-80 rounded-2xl border border-border bg-card shadow-xl overflow-hidden"
            >
              {/* Header */}
              <div className="p-4 border-b border-border/50 bg-gradient-to-r from-primary/5 to-transparent">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-display font-bold text-foreground text-sm">
                    {allDone ? "All Quests Complete! 🎉" : "Getting Started"}
                  </h3>
                  <span className="text-xs font-display font-semibold text-primary">
                    {completed}/{total}
                  </span>
                </div>
                {/* Progress bar */}
                <div className="h-2 rounded-full bg-secondary overflow-hidden">
                  <motion.div
                    className="h-full rounded-full gradient-hero"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  Complete quests to earn Republic Points
                </p>
              </div>

              {/* Quest list */}
              <div className="max-h-72 overflow-y-auto p-2">
                {quests.map((quest, i) => (
                  <motion.button
                    key={quest.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => {
                      if (!quest.completed) {
                        quest.action();
                        handleDismiss();
                      }
                    }}
                    className={`flex items-center gap-3 w-full p-3 rounded-xl text-left transition-all ${
                      quest.completed
                        ? "opacity-60"
                        : "hover:bg-secondary/80 cursor-pointer"
                    }`}
                  >
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-lg flex-shrink-0 ${
                        quest.completed
                          ? "bg-primary/20"
                          : "bg-secondary"
                      }`}
                    >
                      {quest.completed ? (
                        <Check className="h-4 w-4 text-primary" />
                      ) : (
                        <quest.icon className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-display font-medium ${
                          quest.completed
                            ? "line-through text-muted-foreground"
                            : "text-foreground"
                        }`}
                      >
                        {quest.label}
                      </p>
                    </div>
                    <span
                      className={`text-xs font-display font-semibold flex-shrink-0 ${
                        quest.completed ? "text-muted-foreground" : "text-primary"
                      }`}
                    >
                      +{quest.points}
                    </span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuestChecklist;
